import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, Typography, Grid } from "@mui/material";
import Layouts from "../layouts/Layouts";
import { useTranslation } from "react-i18next";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";

const TabPanel = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`equipment-tabpanel-${index}`}
      aria-labelledby={`equipment-tab-${index}`}
      style={{ padding: "20px 0" }}
    >
      {value === index && (
        <Typography component="div" variant="body1">
          {children}
        </Typography>
      )}
    </div>
  );
};

const a11yProps = (index) => ({
  id: `equipment-tab-${index}`,
  "aria-controls": `equipment-tabpanel-${index}`,
});

const Equipments = () => {
  const [value, setValue] = useState(0);
  const [equipmentData, setEquipmentData] = useState([]);
  const { t } = useTranslation();

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    const fetchImages = async () => {
      const equipmentData = [
        {
          category: t("equipment.surgical"),
          items: [
            {
              title: "ZEISS VisuMax 800",
              equipment: t("equipment.machines.machine8Title"),
              description: t("equipment.machines.machine8Description"),
              imagePath: "equipments/visumax_teaser1.jpg",
              extraInfo: t("equipment.machines.machineExtra8"),
            },
            {
              title: "ZEISS OPMI Lumera i",
              equipment: t("equipment.machines.machine1Title"),
              description: t("equipment.machines.machine1Description"),
              imagePath: "equipments/2.jpeg",
              extraInfo: t("equipment.machines.machineExtra1"),
            },
            {
              title: "ZEISS MEL 90",
              equipment: t("equipment.machines.machine2Title"),
              description: t("equipment.machines.machine2Description"),
              imagePath: "equipments/4.jpeg",
              extraInfo: t("equipment.machines.machineExtra2"),
            },
            {
              title: "",
              equipment: t("equipment.machines.machine6Title"),
              description: t("equipment.machines.machine6Description"),
              imagePath: "equipments/8.jpg",
              extraInfo: t("equipment.machines.machineExtra6"),
            },
            {
              title: "",
              equipment: t("equipment.machines.machine7Title"),
              description: t("equipment.machines.machine7Description"),
              imagePath: "equipments/9.jpg",
              extraInfo: t("equipment.machines.machineExtra7"),
            },
          ],
        },
        {
          category: t("equipment.diagnostic"),
          items: [
            {
              title: "ZEISS IOLMaster 500",
              equipment: t("equipment.machines.machine3Title"),
              description: t("equipment.machines.machine3Description"),
              imagePath: "equipments/1.jpeg",
              extraInfo: t("equipment.machines.machineExtra3"),
            },
            {
              title: "ZEISS OCT System",
              equipment: t("equipment.machines.machine4Title"),
              description: t("equipment.machines.machine4Description"),
              imagePath: "equipments/3.jpeg",
              extraInfo: t("equipment.machines.machineExtra4"),
            },
            {
              title: "PENTACAM®",
              equipment: t("equipment.machines.machine5Title"),
              description: t("equipment.machines.machine5Description"),
              imagePath: "equipments/7.png",
              extraInfo: t("equipment.machines.machineExtra5"),
            },
          ],
        },
      ];
      const updatedEquipmentData = await Promise.all(
        equipmentData.map(async (category) => {
          const updatedItems = await Promise.all(
            category.items.map(async (item) => {
              const cachedImageUrl = localStorage.getItem(item.imagePath);
              if (cachedImageUrl) {
                return { ...item, image: cachedImageUrl };
              } else {
                try {
                  const imageRef = ref(storage, item.imagePath);
                  const imageUrl = await getDownloadURL(imageRef);
                  localStorage.setItem(item.imagePath, imageUrl);
                  return { ...item, image: imageUrl };
                } catch (error) {
                  // Image not uploaded to Firebase Storage yet (e.g. new equipment):
                  // fall back to an empty image so one missing file doesn't crash the page.
                  console.warn(`Equipment image not found: ${item.imagePath}`, error);
                  return { ...item, image: "" };
                }
              }
            })
          );
          return { ...category, items: updatedItems };
        })
      );

      setEquipmentData(updatedEquipmentData);
    };

    fetchImages();
  }, [t]);

  return (
    <Layouts footer={2} noheader>
      {/**
      <PageBanner
        title={t("pageBanner.equipments")}
        pageName={t("pageBanner.equipments")}
        pageNumber="5"
      />
     */}{" "}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#f9f9f9",
          padding: { xs: "20px", md: "50px 80px", lg: "50px 240px" },
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography variant="h3" align="center" gutterBottom>
          {t("equipment.equipmentsHeading1")}
        </Typography>
        <Typography
          // variant="subtitle1"
          align="center"
          sx={{
            marginBottom: "20px",
            // color: "var(--color-primary)",
            fontWeight: "700",
          }}
        >
          {t("equipment.equipmentsHeading2")}
        </Typography>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          aria-label="equipment tabs"
          textColor="var(--color-doctor)" // ⬅️ prevent default primary/secondary color
          indicatorColor="var(--color-doctor)" // you already override with sx
          sx={{
            display: "flex",
            width: "100%",
            color: "var(--color-doctor)",
            justifyContent: "center",
            "& .MuiTab-root": {
              textTransform: "uppercase",
              fontSize: { xs: "12px", sm: "14px", md: "16px" },
              fontWeight: 600,
              padding: "10px 15px",
              color: "var(--color-doctor)",
            },
            "& .MuiTab-root.Mui-selected": {
              color: "var(--color-fourth) !important",
              fontWeight: 700,
            },

            "& .MuiTabs-indicator": {
              height: "2px",
              backgroundColor: "#213360",
            },
          }}
        >
          {equipmentData.map((category, categoryIndex) => (
            <Tab
              key={categoryIndex}
              label={category.category}
              {...a11yProps(categoryIndex)}
              sx={{
                display: "flex",
                width: "100%",
              }}
            />
          ))}
        </Tabs>

        {equipmentData.map((category, categoryIndex) => (
          <TabPanel value={value} index={categoryIndex} key={categoryIndex}>
            {category.items.map((equipment, equipmentIndex) => (
              <Grid
                container
                spacing={3}
                alignItems="center"
                justifyContent="center"
                key={equipmentIndex}
                sx={{ marginBottom: "40px" }}
              >
                {equipmentIndex % 2 === 0 ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          width: "100%",
                          height: "420px",
                          backgroundColor: "#fff",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "10px",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <img
                          src={equipment.image}
                          alt={equipment.title}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ fontSize: { xs: "18px", md: "24px" } }}
                      >
                        {equipment.equipment}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        sx={{ fontSize: { xs: "14px", md: "16px" } }}
                      >
                        {equipment.description}
                      </Typography>
                      {equipment.extraInfo && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            fontSize: { xs: "12px", md: "14px" },
                            marginTop: "10px",
                          }}
                        >
                          {equipment.extraInfo}
                        </Typography>
                      )}
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ fontSize: { xs: "18px", md: "24px" } }}
                      >
                        {equipment.equipment}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        sx={{ fontSize: { xs: "14px", md: "16px" } }}
                      >
                        {equipment.description}
                      </Typography>
                      {equipment.extraInfo && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            fontSize: { xs: "12px", md: "14px" },
                            marginTop: "10px",
                          }}
                        >
                          {equipment.extraInfo}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          width: "100%",
                          height: "420px",
                          backgroundColor: "#fff",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "10px",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <img
                          src={equipment.image}
                          alt={equipment.title}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            ))}
          </TabPanel>
        ))}
      </Box>
    </Layouts>
  );
};

export default Equipments;
